// noncompliances.js
// Renders modal CONTENT only — the Modal shell is provided by homePage.js.
// homePage.js calls <ModalContent /> with no props, so this component takes none.
// To connect live data: replace MOCK_NONCOMPLIANCE_DATA with your API call.

import React, { useEffect, useMemo, useState } from "react";
import "../../Modal.css";
import { MOCK_NONCOMPLIANCE_DATA } from "../../../mockData/mockNoncomplianceData";

// ─── Sub-components ───────────────────────────────────────────────────────────

const MetricBox = ({ title, value, variant = "neutral" }) => (
  <div className={`nc-metric-box nc-metric-${variant}`}>
    <div className="nc-metric-title">{title}</div>
    <div className="nc-metric-value">{value}</div>
  </div>
);

const StatusBadge = ({ value }) => {
  const cls = {
    Completed: "badge-completed",
    "Non Conformed": "badge-nonconformed",
    Corrected: "badge-corrected",
    "N/A": "badge-na",
    "Image Submitted": "badge-image",
    Signed: "badge-signed",
    "Pending Signature": "badge-pending-sig",
  };
  if (!value) return null;
  return <span className={`nc-badge ${cls[value] || ""}`}>{value}</span>;
};

const ModuleListItem = ({ moduleItem, selected, onClick }) => (
  <button
    type="button"
    className={`nc-module-item ${selected ? "nc-module-item--selected" : ""}`}
    onClick={onClick}
  >
    <div className="nc-module-item-main">
      <div className="nc-module-wo">{moduleItem.woNum}</div>
      <div className="nc-module-meta">
        {moduleItem.itemNum} &bull; {moduleItem.batchNum}
      </div>
    </div>
    <div className="nc-module-time">
      {new Date(moduleItem.lastSeenAt).toLocaleDateString()}
    </div>
  </button>
);

const StationSummary = ({ station, onSelectTask }) => {
  const totals = useMemo(() => {
    let total = 0,
      completed = 0,
      nonconformed = 0,
      corrected = 0;
    station.tasks.forEach((t) =>
      t.subtasks.forEach((st) => {
        total++;
        if (st.submissionValue === "Completed") completed++;
        else if (st.submissionValue === "Non Conformed") nonconformed++;
        else if (st.submissionValue === "Corrected") corrected++;
      }),
    );
    return {
      total,
      completed,
      nonconformed,
      corrected,
      pct: total ? Math.round((completed / total) * 100) : 0,
    };
  }, [station]);

  return (
    <div className="nc-station-summary">
      <h3 className="nc-section-title">
        Station Summary — {station.stationName}
      </h3>

      <div className="nc-station-metrics">
        <div className="nc-station-stat">
          <div className="nc-station-stat-label">Time in Station</div>
          <div className="nc-station-stat-value">{station.timeInStation}</div>
        </div>
        <div className="nc-station-stat">
          <div className="nc-station-stat-label">Completion Rate</div>
          <div className="nc-station-stat-value">{totals.pct}%</div>
          <div className="nc-station-stat-sub">
            {totals.completed}/{totals.total} steps
          </div>
        </div>
        <div className="nc-station-stat nc-station-stat--alert">
          <div className="nc-station-stat-label">Nonconformances</div>
          <div className="nc-station-stat-value">{totals.nonconformed}</div>
        </div>
        <div className="nc-station-stat">
          <div className="nc-station-stat-label">Corrected</div>
          <div className="nc-station-stat-value">{totals.corrected}</div>
        </div>
      </div>

      <div className="nc-progress-bar-wrap">
        <div
          className="nc-progress-bar-fill"
          style={{ width: `${totals.pct}%` }}
        />
      </div>

      <h4 className="nc-subheading">Tasks in this station</h4>
      <table className="nc-task-table">
        <thead>
          <tr>
            <th>Task</th>
            <th>Steps</th>
            <th>Completed</th>
            <th>Non Conformed</th>
            <th>Corrected</th>
          </tr>
        </thead>
        <tbody>
          {station.tasks.map((t) => {
            const tc = t.subtasks.reduce(
              (a, st) => {
                a.total++;
                if (st.submissionValue === "Completed") a.comp++;
                else if (st.submissionValue === "Non Conformed") a.nc++;
                else if (st.submissionValue === "Corrected") a.corr++;
                return a;
              },
              { total: 0, comp: 0, nc: 0, corr: 0 },
            );
            return (
              <tr
                key={t.taskId}
                className="nc-task-row"
                onClick={() => onSelectTask(t.taskId)}
              >
                <td>
                  {t.taskName}
                  {t.isQA && <span className="nc-qa-tag">QA/QC</span>}
                </td>
                <td>{tc.total}</td>
                <td className="nc-cell-green">{tc.comp}</td>
                <td className={tc.nc > 0 ? "nc-cell-red" : ""}>{tc.nc}</td>
                <td className={tc.corr > 0 ? "nc-cell-amber" : ""}>
                  {tc.corr}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const TaskDetail = ({ task }) => (
  <div className="nc-task-detail">
    <h3 className="nc-section-title">
      {task.taskName}
      {task.isQA && <span className="nc-qa-tag">QA/QC</span>}
    </h3>
    {task.taskDescription && (
      <p className="nc-task-desc">{task.taskDescription}</p>
    )}
    <div className="nc-subtask-list">
      {task.subtasks.map((st) => (
        <div key={st.id} className="nc-subtask-row">
          <div className="nc-subtask-header">
            <span className="nc-subtask-name">{st.name}</span>
            <StatusBadge value={st.submissionValue} />
          </div>
          <div className="nc-subtask-meta">
            <span>{new Date(st.updatedAt).toLocaleString()}</span>
            {st.updatedBy && (
              <>
                <span className="nc-meta-sep">&bull;</span>
                <span>{st.updatedBy}</span>
              </>
            )}
            {st.stepComment && (
              <>
                <span className="nc-meta-sep">&bull;</span>
                <span className="nc-subtask-comment">{st.stepComment}</span>
              </>
            )}
          </div>
          {st.images.length > 0 && (
            <div className="nc-subtask-images">
              {st.images.map((img, ii) => (
                <img
                  key={ii}
                  src={img}
                  alt={`${st.name} ${ii + 1}`}
                  className="nc-thumb"
                />
              ))}
            </div>
          )}
          {st.versionHistory.length > 0 && (
            <details className="nc-version-history">
              <summary>Version history ({st.versionHistory.length})</summary>
              <ul className="nc-version-list">
                {st.versionHistory.map((v) => (
                  <li key={v.versionId} className="nc-version-item">
                    <div className="nc-version-top">
                      <StatusBadge value={v.submissionValue} />
                      <span className="nc-version-date">
                        {new Date(v.submittedAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="nc-version-by">
                      {v.submittedBy} &mdash; {v.notes}
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ─── Main export ──────────────────────────────────────────────────────────────

const Noncompliances = () => {
  const [modules] = useState(() => MOCK_NONCOMPLIANCE_DATA);
  const [search, setSearch] = useState("");
  const [filteredModules, setFilteredModules] = useState(modules);
  const [selectedModuleIndex, setSelectedModuleIndex] = useState(0);
  const [selectedStationId, setSelectedStationId] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  // Filter on search change
  useEffect(() => {
    const q = search.trim().toLowerCase();
    const results = q
      ? modules.filter(
          (m) =>
            m.woNum.toLowerCase().includes(q) ||
            m.batchNum.toLowerCase().includes(q) ||
            m.itemNum.toLowerCase().includes(q),
        )
      : modules;
    setFilteredModules(results);
    setSelectedModuleIndex(results.length ? 0 : -1);
  }, [search, modules]);

  // Default to first station when module changes
  useEffect(() => {
    const mod = filteredModules[selectedModuleIndex];
    if (mod && mod.stations.length) {
      setSelectedStationId(mod.stations[0].stationId);
      setSelectedTaskId(null);
    } else {
      setSelectedStationId(null);
      setSelectedTaskId(null);
    }
  }, [selectedModuleIndex, filteredModules]);

  // Clear task when station changes
  useEffect(() => {
    setSelectedTaskId(null);
  }, [selectedStationId]);

  const selectedModule = filteredModules[selectedModuleIndex] || null;
  const stationOptions = selectedModule ? selectedModule.stations : [];
  const selectedStation =
    stationOptions.find((s) => s.stationId === selectedStationId) || null;
  const taskOptions = selectedStation ? selectedStation.tasks : [];
  const selectedTask =
    taskOptions.find((t) => t.taskId === selectedTaskId) || null;

  return (
    <div className="nc-root">
      {/* Top 3 metric boxes */}
      <div className="nc-top-metrics">
        <MetricBox title="Completed Correctly" value={45} variant="green" />
        <MetricBox title="Unevaluated" value={6} variant="amber" />
        <MetricBox title="Nonconformed" value={7} variant="red" />
      </div>

      {/* Two-column body */}
      <div className="nc-body">
        {/* LEFT — module list */}
        <div className="nc-left">
          <div className="nc-search-row">
            <input
              className="nc-search-input"
              placeholder="Search WO, Batch, or Item..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search modules"
            />
            <span className="nc-search-count">{filteredModules.length}</span>
          </div>
          <div className="nc-module-list">
            {filteredModules.length === 0 && (
              <div className="nc-empty nc-empty--center">
                No modules match your search.
              </div>
            )}
            {filteredModules.map((m, idx) => (
              <ModuleListItem
                key={m.manufacturedItemId}
                moduleItem={m}
                selected={idx === selectedModuleIndex}
                onClick={() => setSelectedModuleIndex(idx)}
              />
            ))}
          </div>
        </div>

        {/* RIGHT — detail panel */}
        <div className="nc-right">
          {!selectedModule ? (
            <div className="nc-empty nc-empty--center">
              Select a module to view details.
            </div>
          ) : (
            <>
              <div className="nc-module-header">
                <div>
                  <div className="nc-module-header-title">
                    {selectedModule.woNum}
                  </div>
                  <div className="nc-module-header-sub">
                    {selectedModule.itemNum} &bull; {selectedModule.batchNum}
                  </div>
                </div>
                <div className="nc-module-header-dates">
                  <div>
                    Created:{" "}
                    {new Date(selectedModule.createdAt).toLocaleDateString()}
                  </div>
                  <div>
                    Last Seen:{" "}
                    {new Date(selectedModule.lastSeenAt).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div className="nc-controls">
                <label className="nc-control-label">
                  <span>Station</span>
                  <select
                    value={selectedStationId || ""}
                    onChange={(e) =>
                      setSelectedStationId(Number(e.target.value))
                    }
                  >
                    {stationOptions.map((s) => (
                      <option key={s.stationId} value={s.stationId}>
                        {s.stationName}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="nc-control-label">
                  <span>Task</span>
                  <select
                    value={selectedTaskId || ""}
                    onChange={(e) => setSelectedTaskId(e.target.value || null)}
                    disabled={!selectedStation}
                  >
                    <option value="">— Station Summary —</option>
                    {taskOptions.map((t) => (
                      <option key={t.taskId} value={t.taskId}>
                        {t.taskName}
                        {t.isQA ? " (QA/QC)" : ""}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="nc-content">
                {selectedTask ? (
                  <TaskDetail task={selectedTask} />
                ) : selectedStation ? (
                  <StationSummary
                    station={selectedStation}
                    onSelectTask={(id) => setSelectedTaskId(id)}
                  />
                ) : (
                  <div className="nc-empty nc-empty--center">
                    Select a station above.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Noncompliances;
